import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import GetPhotoForReactionUsersDocument from "src/api/graphql/__generated__/documents/GetPhotoForReactionUsersDocument";
import { ReactionUserViewFragmentFragment } from "src/api/graphql/__generated__/graphql";
import log from "src/helpers/log";
import useMountEffect from "src/hooks/useMountEffect";
import useStateRef from "src/hooks/useStateRef";
import useSyncRef from "src/hooks/useSyncRef";

const usePhotoReactionUsers = ({
	gatheringId,
	photoId,
}: {
	gatheringId: string;
	photoId: string;
}) => {
	const { data, previousData, refetch, fetchMore, loading } = useQuery(
		GetPhotoForReactionUsersDocument,
		{
			variables: {
				input: {
					photoId,
					gatheringId,
				},
				reactionUsersInput: {
					limit: 15,
				},
			},
			fetchPolicy: "cache-only",
		},
	);
	const photo = data?.getPhoto ?? previousData?.getPhoto;
	const cursorRef = useSyncRef(photo?.reactionUsers?.cursor);

	const [fetchingMore, setFetchingMore, fetchingMoreRef] = useStateRef(false);
	const [refreshing, setRefreshing, refreshingRef] = useStateRef(false);

	const handleFetchMore = useCallback(async () => {
		if (cursorRef.current === "{}") return;
		if (fetchingMoreRef.current) return;
		if (refreshingRef.current) return;
		setFetchingMore(true);
		try {
			await fetchMore({
				variables: {
					input: {
						photoId,
						gatheringId,
					},
					reactionUsersInput: {
						cursor: cursorRef.current,
						limit: 36,
					},
				},
			});
		} catch (err) {
			log.error({ err }, "Error fetching more reaction users");
		} finally {
			setFetchingMore(false);
		}
	}, [
		cursorRef,
		fetchMore,
		gatheringId,
		fetchingMoreRef,
		photoId,
		refreshingRef,
		setFetchingMore,
	]);

	const handleRefresh = useCallback(async () => {
		if (refreshingRef.current) return;
		setRefreshing(true);
		try {
			await refetch({
				input: {
					photoId,
					gatheringId,
				},
				reactionUsersInput: {
					limit: 16,
				},
			});
		} catch (err) {
			log.error({ err }, "Error refreshing reaction users");
		} finally {
			setRefreshing(false);
		}
	}, [gatheringId, photoId, refetch, refreshingRef, setRefreshing]);

	useMountEffect(() => {
		handleRefresh();
	}, []);

	const reactionUsers = useMemo<ReactionUserViewFragmentFragment[]>(() => {
		return (photo?.reactionUsers?.payload ?? []).filter(
			(reactionUser) =>
				reactionUser.user !== null && reactionUser.reaction?.didPrimaryReact === true,
		);
	}, [photo]);

	return {
		numPrimaryReaction: photo?.numPrimaryReaction,
		loading: loading || fetchingMore,
		refreshing,
		refresh: handleRefresh,
		fetchMore: handleFetchMore,
		reactionUsers,
	};
};
export default usePhotoReactionUsers;
